import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useMatter } from '../hooks/useMatter';
import { sponsorRoleNoun } from '../lib/schema-runtime';

interface ImpersonatedClient {
  matterId: string;
  clientName: string;
  /** Display string: subclass + variant, e.g. "600-tourist offshore". */
  visa: string;
  /** The raw subclass on its own: drives the Sponsor/Inviter vocabulary. */
  visaSubclass: string;
  role: 'applicant' | 'sponsor';
}

/**
 * Sticky banner shown when a staff member is in "View as client" mode.
 * Pulls the impersonated matter's client name + visa to make it obvious
 * who they're previewing as.
 */
export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const { actuallyStaff, impersonatedMatterId, impersonatedRole, setImpersonating } = useMatter();
  const [client, setClient] = useState<ImpersonatedClient | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!impersonatedMatterId) {
      setClient(null);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('matters')
        .select(
          `id, visa_subclass, variant,
           primary_contact:contacts!matters_primary_contact_id_fkey (id, given_names, family_name, email),
           sponsor_contact:contacts!matters_sponsor_contact_id_fkey (id, given_names, family_name, email)`,
        )
        .eq('id', impersonatedMatterId)
        .maybeSingle();
      if (cancelled || !data) return;
      const row = data as {
        visa_subclass: string;
        variant: string | null;
        primary_contact: { given_names: string | null; family_name: string | null; email: string } | null;
        sponsor_contact: { given_names: string | null; family_name: string | null; email: string } | null;
      };
      const target = impersonatedRole === 'sponsor' ? row.sponsor_contact : row.primary_contact;
      const name =
        [target?.given_names, target?.family_name].filter(Boolean).join(' ').trim() ||
        target?.email ||
        (impersonatedRole === 'sponsor' ? `this ${sponsorRoleNoun(row.visa_subclass)}` : 'this client');
      const visa = `${row.visa_subclass}${row.variant ? ` ${row.variant}` : ''}`;
      setClient({
        matterId: impersonatedMatterId,
        clientName: name,
        visa,
        visaSubclass: row.visa_subclass,
        role: impersonatedRole,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [impersonatedMatterId, impersonatedRole]);

  if (!actuallyStaff || !impersonatedMatterId) return null;

  const handleExit = () => {
    const matterId = impersonatedMatterId;
    setImpersonating(null);
    if (matterId) {
      navigate(`/matters/${matterId}`, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="bg-amber-500 text-white text-sm sticky top-0 z-50 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Eye size={14} className="shrink-0" />
          <span className="truncate">
            <span className="font-semibold">
              {client?.role === 'sponsor'
                ? client.visaSubclass === '600-tourist'
                  ? 'Editing as inviter.'
                  : client.visaSubclass === '600-family'
                    ? 'Editing as sponsor/recommender.'
                    : 'Editing as sponsor.'
                : 'Editing as applicant.'}
            </span>{' '}
            {client ? (
              <>
                You see what <span className="font-semibold">{client.clientName}</span>{' '}
                ({client.visa}
                {client.role === 'sponsor' ? `, ${sponsorRoleNoun(client.visaSubclass)}` : ''}) sees.
                {' '}Anything you change saves to their real form and is logged against your name.
              </>
            ) : (
              'Loading details...'
            )}
          </span>
        </div>
        <button
          onClick={handleExit}
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 transition-colors font-medium"
        >
          <X size={12} /> Exit preview
        </button>
      </div>
    </div>
  );
}
